<template>
  <div
    v-if="props.blueprint.settings.isInternal || !question.internal"
    :id="`v-form-question-${question.id}`"
    class="v-form-question"
    :class="[`v-form-question--${question.type}`, { 'is-nested': isNested, 'is-internal': question.internal }]"
  >
    <div v-if="internalRibbonVisible" class="v-form-question__ribbon">{{ i18n.internal }}</div>
    <Component :is="internalRibbonVisible ? VCard : 'div'" class="v-form-question__body" v-bind="internalCardProps">
      <div class="v-form-question-main">
        <div class="v-form-question__label">
          {{ question.title }}
          <div class="v-form-question__subtitle">
            <VIconHelp v-if="question.subtitle && question.subtitle.length > 200" :help="question.subtitle" />
            <template v-else>{{ question.subtitle }}</template>
          </div>
        </div>
        <div class="v-form-question__answer">
          <VInput
            v-if="question.type === 'text'"
            v-model="question.answer"
            :error="props.blueprint.errors[question.id]?.answer"
            :maxlength="200"
            :optional="question.optional"
            show-character-limit
            @blur="onQuestionUpdated(question)"
          />
          <VInput
            v-else-if="question.type === 'number'"
            v-model="question.answer"
            :error="props.blueprint.errors[question.id]?.answer"
            :maxlength="5000"
            :optional="question.optional"
            type="number"
            @blur="onQuestionUpdated(question)"
          />
          <VInputUrl
            v-else-if="question.type === 'url'"
            v-model="question.answer"
            :error="props.blueprint.errors[question.id]?.answer"
            :multiple="question.allow_multiple_answers"
            :optional="question.optional"
            @update:model-value="onQuestionUpdated(question)"
          />
          <VTextarea
            v-else-if="question.type === 'textarea'"
            v-model="question.answer"
            :error="props.blueprint.errors[question.id]?.answer"
            label=" "
            :maxlength="15000"
            :optional="question.optional"
            show-character-limit
            @blur="onQuestionUpdated(question)"
          />
          <VFormBlueprintQuestionUpload v-else-if="question.type === 'upload'" :blueprint="props.blueprint" :question>
            <template #file-name="slotProps">
              <slot name="file-name" v-bind="slotProps" />
            </template>
          </VFormBlueprintQuestionUpload>
          <VSelect
            v-else-if="question.type === 'dropdown'"
            v-model="question.answer"
            :error="props.blueprint.errors[question.id]?.answer"
            :multiple="question.allow_multiple_answers"
            :optional="question.optional"
            @update:model-value="onQuestionUpdated(question)"
          >
            <VOption v-for="option in question.options" :key="option.id" :label="option.label" :value="option.id" />
          </VSelect>
          <VDatePicker
            v-else-if="question.type === 'datepicker'"
            v-model="question.answer"
            clearable
            :error="props.blueprint.errors[question.id]?.answer"
            :optional="question.optional"
            @update:model-value="onQuestionUpdated(question)"
          />
          <VRadioGroup
            v-else-if="question.type === 'true_false'"
            v-model="question.answer"
            :error="props.blueprint.errors[question.id]?.answer"
            :optional="question.optional"
            @update:model-value="onQuestionUpdated(question)"
          >
            <VRadio v-for="option in question.options" :key="option.id" :label="option.id">{{ option.label }}</VRadio>
          </VRadioGroup>
          <VFormBlueprintQuestionRepeater v-else-if="question.type === 'repeater'" :blueprint="props.blueprint" :question @updated="onQuestionUpdated($event)">
            <template #answer="slotProps">
              <slot name="answer" v-bind="slotProps" />
            </template>
            <template #after-question-answer="slotProps">
              <slot name="after-question-answer" v-bind="slotProps" />
            </template>
            <template #file-name="slotProps">
              <slot name="file-name" v-bind="slotProps" />
            </template>
          </VFormBlueprintQuestionRepeater>
          <slot v-else name="answer" :question />
          <slot name="after-question-answer" :question />
        </div>
      </div>
      <div v-if="childQuestions.length" class="v-form-question__followup">
        <VFormBlueprintQuestion v-for="childQuestion in childQuestions" :key="childQuestion.id" :blueprint is-nested :question="childQuestion" @updated="onQuestionUpdated($event)">
          <template #answer="slotProps">
            <slot name="answer" v-bind="slotProps" />
          </template>
          <template #after-question-answer="slotProps">
            <slot name="after-question-answer" v-bind="slotProps" />
          </template>
          <template #file-name="slotProps">
            <slot name="file-name" v-bind="slotProps" />
          </template>
        </VFormBlueprintQuestion>
      </div>
    </Component>
  </div>
</template>

<script setup lang="ts" generic="TBlueprint extends Blueprint = Blueprint">
import { computed } from 'vue'

import { type FileLike } from '../types.ts'
import { i18nFormBuilder as i18n } from '../utils/i18n.ts'
import { type Blueprint, type Question } from './FormBuilder/useBlueprint.ts'
import VOption from './Select/VOption.vue'
import VSelect from './Select/VSelect.vue'
import VCard from './VCard.vue'
import VDatePicker from './VDatePicker.vue'
import VFormBlueprintQuestionRepeater from './VFormBlueprintQuestionRepeater.vue'
import VFormBlueprintQuestionUpload from './VFormBlueprintQuestionUpload.vue'
import VIconHelp from './VIconHelp.vue'
import VInput from './VInput.vue'
import VInputUrl from './VInputUrl.vue'
import VRadio from './VRadio.vue'
import VRadioGroup from './VRadioGroup.vue'
import VTextarea from './VTextarea.vue'

interface Props {
  blueprint: TBlueprint
  question: Question
  isNested?: boolean
}

const props = defineProps<Props>()

defineSlots<{
  answer(props: { question: Question }): any
  'after-question-answer'(props: { question: Question }): any
  'file-name'(props: { file: FileLike; index: number; question: Question }): any
}>()

const emit = defineEmits<{
  updated: [question: Question]
}>()

const internalRibbonVisible = computed(() => props.question.internal && !someParentIsInternal(props.question))
const internalCardProps = computed(() => (props.question.internal ? { type: 'primary' } : undefined))

const childQuestions = computed((): Question[] => {
  const question = props.question
  if (question.type !== 'true_false') {
    return []
  }

  const answer = question.answer
  if (!answer) {
    return []
  }

  const option = question.options.find((option) => option.id === answer)

  if (!option) {
    return []
  }

  return props.blueprint.questions.filter((child) => child.option_id === option.id && child.parent_question_id === question.id)
})

function onQuestionUpdated(question: Question) {
  emit('updated', question)

  delete props.blueprint.errors[question.id]

  if (question.type === 'true_false') {
    for (const option of question.options) {
      for (const question of props.blueprint.questions) {
        if (question.option_id === option.id) {
          delete props.blueprint.errors[question.id]
        }
      }
    }
  }
}

function someParentIsInternal(question: Question) {
  if (!question.parent_question_id) {
    return false
  }

  const parentQuestion = props.blueprint.questions.find((blueprintQuestion) => blueprintQuestion.id === question.parent_question_id)
  if (!parentQuestion) {
    return false
  }

  if (parentQuestion.internal) {
    return true
  }

  return someParentIsInternal(parentQuestion)
}
</script>

<style>
.v-form-question {
  padding: 20px 0;

  > .v-card--primary {
    border-color: var(--color-primary);
    color: var(--color-primary);
    font-weight: 500;
  }
}

.v-form-question:not(.is-nested):not(.is-internal) {
  border-top: var(--border);
}

.v-form-question.is-nested {
  margin-left: 40px;

  > .v-form-question__body > .v-form-question-main > .v-form-question__label {
    width: 180px;
  }
}

.v-form-question__ribbon {
  display: inline-block;
  background: var(--color-primary);
  color: var(--color-text-light);
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  padding: 6px;
  margin-bottom: -12px;
  padding-bottom: 12px;
  line-height: 1;
}

.v-form-question-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 80px;
}

.v-form-question__label {
  min-width: 33%;
  flex-basis: 33%;
}

.v-form-question__subtitle {
  color: var(--color-grey-40);
}

.v-form-question__answer {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  > .v-select {
    width: 100%;
  }
}
</style>
